import React, { Suspense, useState, useEffect, useRef } from "react";
import posthog from "posthog-js";
import { useTips, useLoadingTip } from "./hooks/useTips";
import { getLectureNavState } from "./lib/appNav";

const SearchPage = React.lazy(() => import("./pages/SearchPage"));
const AdminPage = React.lazy(() => import("./pages/AdminPage"));
const AboutPage = React.lazy(() => import("./pages/AboutPage"));
const LecturePage = React.lazy(() => import("./pages/LecturePage"));
const OpeningPage = React.lazy(() => import("./pages/OpeningPage"));
const LadderApiPage = React.lazy(() => import("./pages/LadderApiPage"));
const LadderStrategyPage = React.lazy(() => import("./pages/LadderStrategyPage"));
const PlayerPage = React.lazy(() => import("./pages/PlayerPage"));
const PlayersPage = React.lazy(() => import("./pages/PlayersPage"));
const StrategyPage = React.lazy(() => import("./pages/StrategyPage"));
const MapPage = React.lazy(() => import("./pages/MapPage"));
const WallsPage = React.lazy(() => import("./pages/WallsPage"));

const SuspenseLoader = () => {
  const tips = useTips();
  // useLoadingTip: 같은 경로에서 이어지는 다음 로딩 단계(예: LecturePage 자체 로더)와 문장을
  // 공유하고, 7초 넘게 로딩이 계속되면 다른 팁으로 바꿔준다(2026-09-08).
  const tip = useLoadingTip(tips);
  return (
    <div className="flex h-screen w-full flex-col items-center justify-center gap-4 bg-[#0a0a0a] px-6">
      <div className="w-12 h-12 border-4 border-white/10 border-t-primary-blue rounded-full animate-spin"></div>
      {tip && <p className="text-white/40 text-xs text-center max-w-xs md:max-w-2xl">TIP: {tip}</p>}
    </div>
  );
};

const App: React.FC = () => {
  const [currentPath, setCurrentPath] = useState(window.location.pathname);
  // 앱 모드 상세 오버레이(lib/appNav.ts) — history.state를 같이 들고 있어야 "검색 위에 덮인 상세"인지 판정 가능
  const [navState, setNavState] = useState(() => getLectureNavState());

  useEffect(() => {
    const handleLocationChange = () => {
      setCurrentPath(window.location.pathname);
      setNavState(getLectureNavState());
    };
    window.addEventListener('popstate', handleLocationChange);
    return () => window.removeEventListener('popstate', handleLocationChange);
  }, []);

  // /lecture/:id/edit → /admin/lecture/:id 리다이렉트(2026-09-16 사용자 요청) — 정식 경로는
  // /admin 접두사(위 lectureAdminMatch)지만, 모바일에서 주소창 커서가 기본적으로 맨 끝에 있어
  // 뒤에 이어붙이는 쪽이 터치하기 더 편함 — 그래서 접미사 별칭을 하나 더 두고 정식 경로로
  // 보낸다(replace라 이 중간 URL은 뒤로가기 히스토리에 안 남음).
  useEffect(() => {
    const editMatch = currentPath.match(/^\/lecture\/([^/]+)\/edit\/?$/);
    if (editMatch) {
      window.location.replace(`/admin/lecture/${editMatch[1]}`);
    }
  }, [currentPath]);

  const isAdmin = currentPath.startsWith('/admin');
  // /admin/lecture/:id — 검색으로 강의를 보다가 바로 그 강의의 어드민 수정 화면으로 점프하는
  // 지름길(2026-09-16 사용자 요청, "아이디로 검색해서 찾아야 하는" 번거로움 해소). /admin 접두사
  // 관례를 그대로 따름(기존 isAdmin 판정·애널리틱스 제외 로직에 별도 예외 불필요).
  const lectureAdminMatch = currentPath.match(/^\/admin\/lecture\/([^/]+)\/?$/);
  const isAbout = currentPath.startsWith('/about');
  const isLecture = currentPath.startsWith('/lecture/') || currentPath.startsWith('/en/lecture/');
  // 오프닝 북 베타 — 네비 링크 없이 URL 직접 접근만 (숨김 공개)
  const isOpening = currentPath.startsWith('/opening');
  // SC:R 로컬 래더 API 실험 도구 — 개인 관리자 전용, 공개될 일 없어 admin과 동일하게 숨김+추적 제외
  const isLadderApi = currentPath.startsWith('/ladder-api');
  // 리플레이 전략명 추출(실험) — screp 목데이터 기반, 개인 관리자 전용
  const isLadderStrategy = currentPath.startsWith('/ladder-strategy');
  // 선수 페이지(2026-10-20 공개) — 이름 없는 주소만 PlayerPage 안에서 검색 페이지로 폴백. 그래서 아래 isSearchRoute엔 포함
  const isPlayer = /^\/(?:en\/)?player\/[^/]+/.test(currentPath);
  // 맵 상세(/map/:name)·목록(/maps)(+/en/) — 2026-10-21, 어드민 베타. 봇은 functions/map이 같은 데이터로 허브 HTML
  const isMap = /^\/(?:en\/)?(?:map\/[^/]+|maps\/?$)/.test(currentPath);
  // 선수 목록(/players, /en/players — /en/이면 engModeAtom이 영어로 시작)
  const isPlayers = /^\/(?:en\/)?players\/?$/.test(currentPath);
  // 빌드 상세(/strategy/:name)·목록(/strategies) — 2026-10-20, 매치업별로 나눠 보여 줌
  const isStrategy = /^\/(?:en\/)?(?:strategy\/[^/]+|strategies\/?$)/.test(currentPath);
  // 심시티 전용(/walls) — 2026-10-22 어드민 베타, 맵을 바꿔 가며 심시티 보기. 어드민이 아니면 WallsPage 안에서 검색 페이지로
  const isWalls = /^\/(?:en\/)?walls\/?$/.test(currentPath);

  // 이 문서에서 검색 화면이 한 번이라도 떠 있었고, 상세가 그 위에서 열린 경우에만 오버레이로 —
  // 직접 링크로 들어온 상세(아래에 검색 화면 없음)는 기존처럼 단독 페이지
  const isSearchRoute = !isAdmin && !isAbout && !isLecture && !isOpening && !isLadderApi && !isLadderStrategy;
  const searchMountedRef = useRef(isSearchRoute);
  if (isSearchRoute) searchMountedRef.current = true;
  const isLectureOverlay = isLecture && searchMountedRef.current && !!navState?.overSearch;

  // 오버레이 동안 뒤의 검색 화면은 스크롤 잠금 + 제목 보존, 닫히면 스크롤 위치·제목 복원
  useEffect(() => {
    if (!isLectureOverlay) return;
    const scrollY = window.scrollY;
    const title = document.title;
    const root = document.documentElement;
    const prevOverflow = root.style.overflow;
    root.style.overflow = "hidden";
    return () => {
      root.style.overflow = prevOverflow;
      window.scrollTo(0, scrollY);
      document.title = title;
    };
  }, [isLectureOverlay]);

  useEffect(() => {
    // admin/래더API/전략추출은 본인만 쓰는 관리 도구라 외부 분석 도구 추적 대상에서 완전히 제외
    if (isAdmin || isLadderApi || isLadderStrategy) {
      posthog.opt_out_capturing();
    } else {
      posthog.opt_in_capturing();
    }
    (window as any)['ga-disable-G-4NM88MWGKT'] = isAdmin || isLadderApi || isLadderStrategy;
  }, [isAdmin, isLadderApi, isLadderStrategy]);

  return (
    <div className="App min-h-screen bg-gradient-to-br from-[#0a0a0a] to-[#1a1a2e] text-white tracking-tighter antialiased">
      <Suspense fallback={<SuspenseLoader />}>
        {isAdmin ? <AdminPage initialEditLectureId={lectureAdminMatch?.[1]} /> : isAbout ? <AboutPage /> : isLecture && !isLectureOverlay ? <LecturePage key={currentPath} /> : isOpening ? <OpeningPage /> : isLadderApi ? <LadderApiPage /> : isLadderStrategy ? <LadderStrategyPage /> : isPlayer ? <PlayerPage key={currentPath} /> : isMap ? <MapPage key={currentPath} /> : isPlayers ? <PlayersPage /> : isStrategy ? <StrategyPage key={currentPath} /> : isWalls ? <WallsPage /> : <SearchPage />}
      </Suspense>
      {isLectureOverlay && (
        // 별도 Suspense — 상세 청크 로딩 중에도 뒤의 검색 화면이 폴백으로 바뀌지 않게
        <div className="fixed inset-0 z-[60] overflow-y-auto overscroll-contain bg-[#0a0a0a]">
          <Suspense fallback={<SuspenseLoader />}>
            <LecturePage key={currentPath} />
          </Suspense>
        </div>
      )}
    </div>
  );
};

export default App;
